import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { ReviewCard, Stars } from "../components/ReviewCard";
import { Placeholder, isPlaceholder } from "../components/Placeholder";
import { CtaBanner } from "../components/CtaBanner";
import { reviews, site, btnPrimary } from "../config/siteConfig";

export default function ReviewsPage() {
  const hasReviewLink = !isPlaceholder(site.googleReviewUrl);

  return (
    <>
      <Seo
        title="Reviews | Ansar Public School Mundeke Goraya, Daska"
        description="Read verified family reviews of Ansar Public School Mundeke Goraya. Only genuine, verified reviews are published."
      />
      <PageHero
        chapter="01"
        eyebrow="Reviews"
        title="What Families Say"
        description="We never publish invented testimonials. Every review shown here will be a verified review from a real parent or guardian."
        crumb="Reviews"
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <Reveal>
            <div
              data-testid="google-review-cta"
              className="flex flex-col items-start gap-5 rounded-[2rem] border border-brand-line bg-brand-cream p-8 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-amber">
                  <Star className="h-6 w-6 fill-white text-white" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="font-display text-xl font-bold text-brand-ink">
                    Are You a Parent at Our School?
                  </h2>
                  <p className="mt-1 max-w-xl text-sm leading-relaxed text-brand-muted">
                    Your honest Google review helps other families in Mundeke Goraya and
                    Daska learn about the school.
                  </p>
                </div>
              </div>
              {hasReviewLink ? (
                <a
                  href={site.googleReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-google-review-button"
                  className={btnPrimary}
                >
                  Leave a Google Review
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : (
                <Placeholder>{site.googleReviewUrl}</Placeholder>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <Reveal>
          <SectionHeading
            chapter="02"
            eyebrow="Verified Reviews"
            title="Family Reviews"
            description="The cards below are editable placeholders. They will be replaced with verified Google reviews — including reviewer name, rating, review text, date and a link to the original review."
          />
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={i * 0.08} className="h-full">
              <ReviewCard review={r} index={i} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-brand-line bg-white p-8 text-center">
            <Stars rating={5} />
            <p className="max-w-xl text-sm leading-relaxed text-brand-muted">
              How reviews appear here: reviewer name, star rating, review text, review
              date and an optional link to the original Google review. Nothing is
              published without verification.
            </p>
            <Link
              to="/contact"
              data-testid="reviews-contact-link"
              className="text-sm font-semibold text-brand transition-colors hover:text-brand-dark"
            >
              Have a question instead? Contact the school
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:pb-24">
        <CtaBanner
          idPrefix="reviews-admissions"
          title="Ready to Explore Admission?"
          text="Speak with our school team to learn about available classes, admission requirements, timings and the next steps."
          primaryLabel="Start an Admission Enquiry"
          primaryTo="/admissions"
          secondaryLabel="Call the School"
          secondaryHref={site.phoneHref}
        />
      </section>
    </>
  );
}
